import { Button, Stack, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import React from "react";
import { floorToQuarterHour } from "../../../utils/timeUtils";

interface CurrentTimeControlProps {
  currentTime: Date;
  startDate: Date;
  endDate: Date;
  onTimeChange: (date: Date) => void;
  onNowClick?: () => void;
  isScrolledAway?: boolean;
}

export const CurrentTimeControl: React.FC<CurrentTimeControlProps> = ({
  currentTime,
  startDate,
  endDate,
  onTimeChange,
  onNowClick,
  isScrolledAway = false,
}) => {
  // Check if current time is already at "now" (within 15-minute interval)
  const isAtNow = React.useMemo(() => {
    const now = floorToQuarterHour(new Date());
    const currentFloored = floorToQuarterHour(currentTime);

    // Compare times (within 1 second tolerance for floating point)
    return Math.abs(now.getTime() - currentFloored.getTime()) < 1000;
  }, [currentTime]);

  // Button is disabled only when we're at "now" AND not scrolled away
  const isNowButtonDisabled = isAtNow && !isScrolledAway;

  // Jump to now
  const handleJumpToNow = () => {
    if (onNowClick) {
      onNowClick();
    } else {
      onTimeChange(floorToQuarterHour(new Date()));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={1}>
        {/* Main controls row */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Current Time Picker */}
          <DateTimePicker
            label="Current Time"
            value={currentTime}
            onChange={(newValue) => {
              if (newValue) {
                onTimeChange(newValue);
              }
            }}
            ampm={false}
            minutesStep={15}
            slotProps={{
              textField: {
                size: "small",
                sx: { minWidth: 200 },
              },
            }}
          />

          {/* Now button */}
          <Button
            variant="contained"
            size="small"
            onClick={handleJumpToNow}
            disabled={isNowButtonDisabled}
            sx={{ minWidth: 60 }}
          >
            Now
          </Button>
        </Stack>

        {/* Helper text showing the calculated range */}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          Viewing: {format(startDate, "MMM d, yyyy")} -{" "}
          {format(endDate, "MMM d, yyyy")}
        </Typography>
      </Stack>
    </LocalizationProvider>
  );
};
